import { useContext, useState, useEffect, useRef, useReducer } from "react";
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  useTheme,

} from "@mui/material";
import { useCookies } from "react-cookie";
import DeviceService from "../../services/DeviceService";
import { ManualParmInput } from "../device/ManualParmInput";
import { AlarmProfileSelectMemo } from "../../pages/Home/Device/AlarmProfileSelect";
import { useTranslation } from "react-i18next";
import { TimelineBoxMemo } from "./TimelineBox";
import { ScoringBox } from "./ScoringBox";
import { CareSummaryProps } from "./interfaces";
import { almProfileEntry } from "./interfaces";
import useSentioSocket from "../../hooks/useSentioSocket";
import { GranularitySelector } from "./GranularitySelector";
import { SelectParameters } from "../device/SelectParameters";
import { DialogCaseId } from "../DialogCaseId";
import { Loading } from "../../components/Loading";
//import moment from "moment";
import { UserContext } from "../../context/UserContext";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'; // Import the caret-down icon
import InterventionDialog from "../InterventionDialog";
import { Intervention } from "../../components/chart/interfaces";
import { formatTimestamp } from "../device/TopPartMonitorNEWS";


export const GRAPH_MARGIN = 64;
export const graphUnitHeightPx = 18;
const MAX_REPORT_DURATION_DAYS = 10;
const MAX_REPORT_DURATION_HOURS = MAX_REPORT_DURATION_DAYS * 24;
export const MAX_REPORT_DURATION_SEC = MAX_REPORT_DURATION_HOURS * 60 * 60; // seconds
const MIN_REPORT_DURATION_SEC = 1 * 60 * 30; // 30 minutes

const scoringColors = {
  minor: "#FFFF0055",
  major: "#F39C1255",
  critical: "#FF000055",
};

/*
    step: interval between steps on the scale
    max: max number of "step" intervals : max plot value = step * max
    min: min number of "step" intervals : min plot value = step * min
 */
const unitSteps: { [key: string]: {} } = {
  RR: { step: 5, max: 45, min: 0 },
  SPO2: { step: 2, max: 102, min: 86 },
  NIBP_Systole: { step: 10, max: 240, min: 40 },
  HR: { step: 10, max: 150, min: 30 },
  EtCO2: { step: 10, max: 80, min: 0 },
  EtCO2_m: { step: 10, max: 80, min: 0 },
  EtCO2_k: { step: 1, max: 14, min: 0 },
  ACVPU: { step: 1, max: 5, min: 0 },
  RespDistress: { step: 1, max: 4, min: 0 },
  Temp1: { step: 1, max: 44, min: 30 },
  Temp1_F: { step: 2, max: 112, min: 86 },
  Temp1_C: { step: 1, max: 44, min: 30 },
  Act: { step: 1, max: 10, min: 0 },
};

export interface DropdownOption {
  value: string;
  label: string;
}

interface TrendData {
  data: {
    [key: string]: string[] | number[] | undefined;
    timestamps?: number[];
  };
  device_def: {
    parameters: {
      [key: string]: any;
    };
  };
}

export const CareSummaryInfo = ({
  device,
  initialAlmProfileDef,
  alarmProp,
  interventions,
  updateInterventions,
}: CareSummaryProps) => {
  //console.log ("CareSummaryInfo", device);
  //console.log ("alarmProp: ", alarmProp);
  //console.log ("initia def", initialAlmProfileDef);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const context = useContext(UserContext);
  const [user, ] = useState(context?.user);
  // Type assertion for user with scoring_charts property
  const userWithScoringCharts = user;
  const theme = useTheme();

  const { t } = useTranslation();
  const [cookies] = useCookies(["access_token"]);
  const [activeAlmProfile, setActiveAlmProfile] =
    useState<almProfileEntry>(initialAlmProfileDef);

  const [openNewChartDialog, setOpenNewChartDialog] = useState(false);
  const [openExportSnackbar, setOpenExportSnackbar] = useState(false);
  const [openCaseDialog, setOpenCaseDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [parmInterval, setParmInterval] = useState(10);
  const [ACVPU_val, ] = useState(
    device.dynamic_data?.ACVPU?.value ? device.dynamic_data?.ACVPU?.value : ""
  );
  const [RespDistress_val, setRespDistress_val] = useState(
    device.dynamic_data?.RespDistress?.value
      ? device.dynamic_data?.RespDistress?.value
      : ""
  );
  const [chart_max_ts, setChart_max_ts] = useState<number>(0);
  const [chart_min_ts, setChart_min_ts] = useState<number>(NaN);
  const [dataPacket, setDataPacket] = useState<any>(null);
  const currentTs = useRef(0);
  const exportEnabled = useRef(false);
  const max_timestamp = useRef<number>(NaN);
  const min_timestamp = useRef<number>(NaN);
  const [useScoringChart, setUseScoringChart] = useState (user?.scoring_charts);
  const [useNeuroparms, ] = useState (user?.neuro_parms);
  const useActivityLevel = false; // for now.
  const [dropdownOpen, setDropdownOpen] = useState(false); // State for the dropdown dialog
  const [isDimmed, setIsDimmed] = useState(false);
  const [patientInfo, setPatientInfo] = useState<any>({});

  const handleRefreshInterventions = () => {
    updateInterventions();
  };


  useEffect (() => {
    if (!userWithScoringCharts?.scoring_charts) {
      setActiveAlmProfile(alarmProp.alarm_profile_defs["NONE"]);
    }
    setUseScoringChart(userWithScoringCharts?.scoring_charts || false);
  }, [userWithScoringCharts?.scoring_charts, alarmProp.alarm_profile_defs]);



  // State for parmHistory
  const [parmHistory, setParmHistory] = useState<TrendData>({
    data: {},
    device_def: { parameters: {} },
  });

  
  const [selectedIntervention, setSelectedIntervention] = useState<string>('');
  

  const lookup_almProfile = (code: string, unit='') => {
    for (var i = 0; i < activeAlmProfile?.alarms?.length; i++) {
      const p = activeAlmProfile.alarms[i];
      const lookupcode = unit !== '' ? code + "_" + unit : code;
      if (p.code === code) {
        p.unitStep = unitSteps[lookupcode];

        switch (unit) {
          // EtCO2 units
          case 'k': {
            p.name = "EtCO2 (kPa)";
            break;
          }
          case "m": {
            p.name = "EtCO2 (mmHg)";
            break;
          }

          // Temperature units
          case "F": {
            p.name = "Temperature (F)";
            break;
          }
          case "C": {
            p.name = "Temperature (C)";
            break;
          }
        } // switch
        //if (unit === 'k') {
        //  p.name = "EtCO2 (kPa)";
        //}
        return p;
      }
    }
    return null;
  }; // lookup_almProfile

  // Set up streaming of live vital signs
  useSentioSocket({
    device,
    setDataPacket,
    mode: "stream_trend",
    // Remove intervention_id from the socket call
  });

  // Refs for parameter histories
  const RR_history = useRef<string[] | number[]>([]);
  const O2_history = useRef<string[] | number[]>([]);
  const Temp1_history = useRef<string[] | number[]>([]);
  const ts_history = useRef<number[]>([]);
  const NIBP_history = useRef<string[] | number[]>([]);
  const HR_history = useRef<string[] | number[]>([]);
  const EtCO2_history = useRef<string[] | number[]>([]);
  const ACVPU_history = useRef<string[] | number[]>([]);
  const RespDistress_history = useRef<string[] | number[]>([]);
  const Act_history = useRef<string[] | number[]>([]);

  const RR_alm = useRef(lookup_almProfile("RR"));
  const O2_alm = useRef(lookup_almProfile("SPO2"));
  const Temp1_alm = useRef(lookup_almProfile("Temp1", device.temp_unit));
  const NIBP_alm = useRef(lookup_almProfile("NIBP_Systole"));
  const HR_alm = useRef(lookup_almProfile("HR"));
  //var EtCO2_alm = useRef(lookup_almProfile("EtCO2", device.co2_unit));
  const [EtCO2_alm, setEtCO2_alm] = useState(lookup_almProfile("EtCO2", device.co2_unit));
  const ACVPU_alm = useRef(lookup_almProfile("ACVPU"));
  const RespDistress_alm = useRef(lookup_almProfile("RespDistress"));
  const Act_alm = useRef(lookup_almProfile("Act"));
  const [noData, setNoData] = useState(false);
  var ALL_PARAMS: string[] = [];
  const [availableParams, setAvailableParams] = useState<string[]>([]);
  const [showInterventionSelector, setShowInterventionSelector] = useState(false);

  ALL_PARAMS = [];
  for (var i = 0; i < activeAlmProfile.alarms.length; i++) {
    // don't allow IBP
    if (activeAlmProfile.alarms[i].code !== "IBP1_Systole")
      ALL_PARAMS.push(activeAlmProfile.alarms[i].code);
  }

  // Fetch parmHistory when selectedIntervention changes
  useEffect(() => {
    //if (selectedIntervention === undefined) return;
    
    let isMounted = true; // Track if the component is mounted
    
    const fetchParmHistory = async () => {
      try {
        const res = await DeviceService.getInterventionData(
          {
            device_id: device.device_id,
            intervention_id:
              selectedIntervention === "None" ? null : selectedIntervention,
            data_type: "trend",
          },
          cookies.access_token
        );
      
        if (isMounted) {
          // Only update state if the component is still mounted
          if (res?.data) {
            //console.log ("parm history:", res.data);
            setParmHistory(res.data);
            setPatientInfo(res.data.data.patientInfo);

          }
        }

      } catch (error) {
        console.error("Error fetching parmHistory:", error);
      }
    };
    
    fetchParmHistory();
    
    return () => {
      isMounted = false; // Cleanup function to prevent state updates after unmount
    };
  }, [selectedIntervention, device.device_id, cookies.access_token]);
  

  // Update histories when parmHistory changes
  useEffect(() => {
    //console.log ("parmHistory:", parmHistory);
    if (!parmHistory || !parmHistory.data) return;

    // Reset histories
    RR_history.current = parmHistory.data["Resp"] || [];
    O2_history.current = parmHistory.data["SpO2"] || [];
    Temp1_history.current = parmHistory.data["Temp1"] || [];
    ts_history.current = parmHistory.data["timestamps"] || [];
    NIBP_history.current = parmHistory.data["NIBP_Systole"] || [];
    HR_history.current = parmHistory.data["HR"] || [];
    EtCO2_history.current = parmHistory.data["EtCO2"] || [];
    ACVPU_history.current = parmHistory.data["ACVPU"] || [];
    RespDistress_history.current = parmHistory.data["RespDistress"] || [];
    Act_history.current = parmHistory.data["Act"] || [];

    if (ts_history.current.length === 0) {
      min_timestamp.current = Date.now();
      max_timestamp.current =
        min_timestamp.current + MIN_REPORT_DURATION_SEC * 1000;
      setNoData(true);
    } else {
      min_timestamp.current = ts_history.current[0] * 1000; // Convert to milliseconds
      const last_idx = ts_history.current.length - 1;
      max_timestamp.current = ts_history.current[last_idx] * 1000;
      setNoData(false);
    }

    setChart_min_ts(min_timestamp.current);
    setChart_max_ts(max_timestamp.current);

    // Force re-render
    forceUpdate();
  }, [parmHistory]);

  useEffect(() => {
    RR_alm.current = lookup_almProfile("RR");
    O2_alm.current = lookup_almProfile("SPO2");
    Temp1_alm.current = lookup_almProfile("Temp1");
    NIBP_alm.current = lookup_almProfile("NIBP_Systole");
    HR_alm.current = lookup_almProfile("HR");
    setEtCO2_alm (lookup_almProfile("EtCO2", device.co2_unit));
    ACVPU_alm.current = lookup_almProfile("ACVPU");
    RespDistress_alm.current = lookup_almProfile("RespDistress");
    Act_alm.current = lookup_almProfile("Act");

    forceUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAlmProfile]);

  const [selectedAlarmProfileKey, setSelectedAlarmProfileKey] = useState<string>(
    device.alarm_profile_key
  );

  // When dataPacket updates
  useEffect(() => {
    if (dataPacket === null || Object.keys(dataPacket).length === 0) {
      return;
    }
    console.log ("data packet:", dataPacket);
    // Get the intervention_id from the dataPacket
    const packetInterventionId = dataPacket.interventionId;

    //console.log("Packet Intervention ID:", packetInterventionId);
    //console.log("Dropdown Options:", interventionList);
    //console.log ("Packet:", dataPacket);

    // Only update if packetInterventionId is defined and different from the last intervention in the dropdown
    if (interventions[interventions.length - 1]) {
      if (packetInterventionId !== interventions[interventions.length - 1].intervention_id) {
        console.log("Updating selectedIntervention to", packetInterventionId);
    
        // Ask the parent component to retrieve a new intervention list.
        handleRefreshInterventions()
    
        return; // Exit the useEffect to wait for selectedIntervention change
      }
    
    }

    // Only update the graph if the "live" intervention is selected.
    var isLiveIntervention = (packetInterventionId === selectedIntervention) && 
    (selectedIntervention === interventions[interventions.length - 1].intervention_id)

    if (!isLiveIntervention) {
      return;
    }

    currentTs.current = Date.now();

    if (noData) {
      setNoData(false);
    }

    // Update timestamp
    const packet_date = new Date(dataPacket.timestamp);
    const packet_ts = packet_date.getTime() / 1000; // In seconds
    ts_history.current = [...ts_history.current, packet_ts];
    max_timestamp.current = packet_ts * 1000; // Convert to milliseconds

    // Update parameters
    O2_history.current = [...O2_history.current, dataPacket?.SpO2 ?? "--"];
    RR_history.current = [...RR_history.current, dataPacket?.Resp ?? "--"];
    NIBP_history.current = [...NIBP_history.current, dataPacket?.NIBP ?? "--"];
    HR_history.current = [...HR_history.current, dataPacket?.HR ?? "--"];
    Temp1_history.current = [...Temp1_history.current, dataPacket?.Temp ?? "--"];
    EtCO2_history.current = [...EtCO2_history.current, dataPacket?.EtCO2 ?? "--"];
    ACVPU_history.current = [...ACVPU_history.current, dataPacket?.ACVPU ?? "--"];
    //if (dataPacket?.ACVPU) {
    //  ACVPU_history.current = [...ACVPU_history.current, dataPacket.ACVPU];
    //}
    if (dataPacket?.RespDistress) {
      RespDistress_history.current = [
        ...RespDistress_history.current,
        dataPacket.RespDistress,
      ];
    }
    Act_history.current = [...Act_history.current, dataPacket?.Act ?? "--"];

    // Update manual parameters
    //setACVPU_val(dataPacket.ACVPU?.value ?? "");
    setRespDistress_val(dataPacket.RespDistress?.value ?? "");

    // Update patient info
    setPatientInfo(dataPacket.patientInfo);

    // Force re-render if necessary
    forceUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPacket]);



  const current_ts = currentTs.current; // Date.now()

  if (isNaN(max_timestamp.current) || isNaN(min_timestamp.current)) {
    exportEnabled.current = false;
  } else {
    exportEnabled.current = true;
  }

  // Check if we should adjust the lower timestamp. That should only happen once.
  if (!isNaN(min_timestamp.current)) {
    if (isNaN(chart_min_ts) || min_timestamp.current < chart_min_ts) {
      setChart_min_ts(min_timestamp.current);
    }
  }

  // Check the upper timestamp.
  const CHART_RIGHT_HORIZON = 60 * 1000; // One minute "margin" to the right
  if (
    !isNaN(min_timestamp.current) &&
    !isNaN(max_timestamp.current) &&
    max_timestamp.current >= chart_max_ts - CHART_RIGHT_HORIZON
  ) {
    const CHART_EXPANSION_AMOUNT = 15 * 60 * 1000; // Increment the chart every 15 minutes
    setChart_max_ts(max_timestamp.current + CHART_EXPANSION_AMOUNT);
  }


  const handleSetCaseID = () => {
    setOpenCaseDialog(true);
  };

  const handleStartExport = () => {
    setOpenExportDialog(true);
  };
  const handleCancelExport = () => {
    setOpenExportDialog(false);
  };

  const handleCSVExport = () => {
    setOpenExportDialog(false);
    handleFileExport("csv");
  };

  const handlePDFExport = () => {
    setOpenExportDialog(false);
    handleFileExport("pdf");
  };

  const handleJSONExport = () => {
    setOpenExportDialog(false);
    handleFileExport("json");
  };

  const handleXMLExport = () => {
    setOpenExportDialog(false);
    handleFileExport("xml");
  };

  const handleOpenDropdownDialog = () => {
      setDropdownOpen(true);
  };

  const handleCloseDropdownDialog = () => {
      setDropdownOpen(false);
  };


  const handleFileExport = (export_type: string) => {
    console.log ("Export parameters:", selectedParameters)
    const min_time = new Date(chart_min_ts).toISOString();
    const max_time = new Date(chart_max_ts).toISOString();
    // Get the local timezone offset from the browser
    const my_timezone = new Date().getTimezoneOffset();

    const almProfileHeader = activeAlmProfile.headline;
    const data = {
      device_id: device.device_id,
      device_type: device.device_type,
      device_model: device.device_model,
      chart_type: almProfileHeader, // must be the headline
      patientinfo: patientInfo, //dataPacket !== null ? dataPacket.patientInfo : null,
      start_time: min_time,
      end_time: max_time,
      tzoffset: my_timezone,
      case_id: device.case_id,
      file_type: export_type,
      interval: parmInterval,
      parameters: selectedParameters,
      intervention_id:
        selectedIntervention === "None" ? null : selectedIntervention,
    };

    setOpenExportSnackbar(true);
    console.log("EXPORT:", data);
    DeviceService.exportToFile(data, cookies.access_token).then((res: any) => {
      console.log ("Result:", res);
      var fileOfBlob = new File([res.data], "chart.tmp");

      const fileURL = window.URL.createObjectURL(fileOfBlob);
      // Setting various property values
      let alink = document.createElement("a");
      alink.href = fileURL;

      const d = new Date();
      const mm = d.getMonth() < 10 ? "0" + d.getMonth() : d.getMonth();
      const dd = d.getDate() < 10 ? "0" + d.getDate() : d.getDate();
      const HH = d.getHours() < 10 ? "0" + d.getHours() : d.getHours();
      const MM = d.getMinutes() < 10 ? "0" + d.getMinutes() : d.getMinutes();

      const ftime =
        d.getFullYear() + "-" + mm + "-" + dd + "_" + HH + "." + MM;

      alink.download = device.device_id + "_" + ftime + "." + export_type;
      alink.click();
      setOpenExportSnackbar(false);
    });
  };

  const handleCloseYes = () => {
    // Send msg to back-end to erase hotstorage data for this device.
    const data = { device_id: device.device_id };
    DeviceService.eraseHotstorage(data, cookies.access_token).then(
      (res: any) => {
        window.location.reload();
      }
    );
    setOpenNewChartDialog(false);
  };

  const handleCloseNo = () => {
    setOpenNewChartDialog(false);
  };

  const isPharlap = localStorage.getItem("SYSTEM_TYPE") === "PHARLAP";

  const LIMITED_PARAMS = ["HR", "O2", "NIBP", "Resp"];
  const [selectedParameters, setSelectedParameters] = useState<string[]>([]);

  useEffect (() => {
    var use_params = [];
    for (var i = 0; i < activeAlmProfile.alarms.length; i++) {
      // don't allow IBP. Don't allow RespDistress or ACVPU if not using neuroparms
      if (activeAlmProfile.alarms[i].code === "RespDistress" && !useNeuroparms)
        continue;
      if (activeAlmProfile.alarms[i].code === "ACVPU" && !useNeuroparms) continue

      // Don't allow ACT parmeter if not using activity level
      if (activeAlmProfile.alarms[i].code === "Act" && !useActivityLevel) continue

      if (activeAlmProfile.alarms[i].code !== "IBP1_Systole")
        use_params.push(activeAlmProfile.alarms[i].code);
    }
  
    setAvailableParams(use_params);
  }, [useNeuroparms, activeAlmProfile.alarms, useActivityLevel]);


  // Upadate the saved parameters so the web page loads with the correct parameters next time.
  useEffect(() => {
    if (selectedParameters.length > 0) {
      localStorage.setItem("caresummary-" + device.device_id, JSON.stringify (selectedParameters));
      //console.log ("Storing parameters:", selectedParameters);
    }
  }, [selectedParameters, device.device_id]);




  // Load the saved parameters on page load.
  useEffect(() => {
    const savedParams = JSON.parse(
      // @ts-ignore
      localStorage.getItem("caresummary-" + device.device_id)
    );
    //console.log ("localstorage for caresummary-" + device.device_id, localStorage.getItem("caresummary-" + device.device_id))  
    //console.log ("Loading parameters:", savedParams);
    if (savedParams !== null) {
      setSelectedParameters (savedParams)
    } else {
      setSelectedParameters (["HR", "SPO2", "RR", "EtCO2"]);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowInterventionSelector(interventions.length > 0);
  }, [interventions]);


  // Handle change event
  const handleInterventionSelection = (value: string) => {
    setSelectedIntervention(value);
    //console.log("Selected value:", value);
    if (value === interventions[interventions.length - 1].intervention_id) {
      setIsDimmed(false);
    } else {
      setIsDimmed(true);
    }
    setDropdownOpen(false);
  };
  
  const handleSelectLiveIntervention = () => {
    if (!interventions) {
        return;
    }
    const lastIntervention = interventions[interventions.length - 1];
    if (setSelectedIntervention) {
        setSelectedIntervention(lastIntervention.intervention_id || '');
    }
    setIsDimmed (false);
    setDropdownOpen(false);
  };

  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);
  useEffect(() => {
    //console.log ("Interventions:", interventions);
    if (!interventions) {
        return;
    }

    const hasNullValue = interventions.some((intervention: Intervention) => intervention.intervention_id === null);

    const dropOptions: DropdownOption[] = interventions
        .filter((intervention: Intervention) => intervention.intervention_id !== null)
        .map((intervention: Intervention): DropdownOption => ({
            value: intervention.intervention_id as string,
            label: `${intervention.intervention_id} : ${formatTimestamp(intervention.min_time)}`,
        }))

    if (hasNullValue) {
        dropOptions.unshift({ value: "None", label: t("None") });
    }
    if (setSelectedIntervention) {
        setSelectedIntervention(dropOptions[dropOptions.length - 1]?.value || '');
    }
    setDropdownOptions(dropOptions);
    //updateInterventions(interventions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventions]);


  //console.log ("interventionList:", interventionList);

  if (isNaN(chart_min_ts) || isNaN(chart_max_ts)) {
    return <Loading />;
  }
  //console.log ("SelectedPArameters:", selectedParameters)
  //console.log ("showInterventionSelector", interventions,  showInterventionSelector, selectedIntervention, isDimmed);

  
  return (
    <div
    style={{
      backgroundColor: isDimmed ? '#bbbb66' : 'transparent', // Toggle yellow background
      minHeight: '100vh', // Ensure it covers the full viewport height
      width: '100%',
      transition: 'background-color 0.3s ease-in-out', // Smooth transition effect
    }}
  >

    <Grid container>
      <Grid container direction="row" mb={1} mt={2}>
        <Grid  item xs={2} container alignItems={"center"} component="div">
          <SelectParameters
            deviceId={device.device_id}
            selectableParameters={isPharlap ? LIMITED_PARAMS : availableParams}
            selectedParameters={selectedParameters}
            setSelectedParameters={setSelectedParameters}
            maxParameters={
              isPharlap ? LIMITED_PARAMS.length : ALL_PARAMS.length
            }
            type="caresummary"
            width="95%"
            margin={0}
          />
        </Grid>
        
        <Grid item xs={2}>
          <Button
            variant="contained"
            onClick={handleStartExport}
            disabled={openExportSnackbar || !exportEnabled.current}
          >
            {t("EXPORT CHART")}
          </Button>
        </Grid>
        <Grid item xs={2}>
        </Grid>

        <Grid item xs={3}>
          { useNeuroparms && (<ManualParmInput
            device={device}
            ACVPU_val={ACVPU_val}
            RespDistress_val={RespDistress_val}
          />)}
        </Grid>
        {false && (
          <Grid item xs={2}>
            <Button variant="contained" onClick={handleSetCaseID}>
              {t("set_case_id")}
            </Button>
          </Grid>
        )}

        {showInterventionSelector && (
            <Grid container item xs={3} justifyContent="flex-end" style={{ marginTop: "10px" }}>
                <Typography
                    color={theme.palette.primary.main}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "right", // Right-align the text
                    }}
                    onClick={handleOpenDropdownDialog}
                >
                    {selectedIntervention ? selectedIntervention : t("Select Intervention")}
                    <ArrowDropDownIcon 
                        style={{ marginLeft: "4px", verticalAlign: "middle" }} // Ensure the icon is centered vertically
                    />
                </Typography>
            </Grid>
        )}
        <InterventionDialog
            open={dropdownOpen}
            onClose={handleCloseDropdownDialog}
            dropdownOptions={dropdownOptions}
            selectedIntervention={selectedIntervention || ''}
            onSelectIntervention={handleInterventionSelection}
            onSelectLive={handleSelectLiveIntervention}
        />



      </Grid>
      <Grid container direction="row">
        <Grid item xs={2}></Grid>
        <Grid item xs={2}>
          Device: {device.device_id}
        </Grid>
        <Grid item xs={3}>
          Patient Name: {patientInfo?.last_name},{" "}
          {patientInfo?.first_name}
        </Grid>
        <Grid item xs={1}>
          Age: {patientInfo?.age}
        </Grid>
        <Grid item xs={1}>
          Gender: {patientInfo?.gender}
        </Grid>
        <Grid item xs={1}>
          Bed: {patientInfo?.bed_no}
        </Grid>
        <Grid item xs={2}>
          Case ID: {device.case_id}
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 3 }}>
        <TimelineBoxMemo min_ts={chart_min_ts} max_ts={chart_max_ts}>
          { useScoringChart && <AlarmProfileSelectMemo
            profileList={alarmProp.alarm_profiles}
            profileDefs={alarmProp.alarm_profile_defs}
            selectedProfile={selectedAlarmProfileKey}
            setProfileDef={setActiveAlmProfile} // the profile itself
            setProfileSelect={setSelectedAlarmProfileKey} // the key
            title={t("Chart Type")}
          />}
        </TimelineBoxMemo>
        {noData && (
          <Typography variant="h4" color="text.primary">
            {t("no_data_to_display")}
          </Typography>
        )}
        {selectedParameters.includes("SPO2") && (
          <ScoringBox
            parm={O2_history.current}
            ranges={O2_alm.current}
            timestamps={ts_history.current}
            colors={scoringColors}
            min_ts={chart_min_ts}
            max_ts={chart_max_ts}
            current_ts={current_ts}
          />
        )}
        {selectedParameters.includes("NIBP_Systole") && (
          <ScoringBox
            parm={NIBP_history.current}
            ranges={NIBP_alm.current}
            timestamps={ts_history.current}
            colors={scoringColors}
            min_ts={chart_min_ts}
            max_ts={chart_max_ts}
            current_ts={current_ts}
          />
        )}
        {selectedParameters.includes("HR") && (
          <ScoringBox
            parm={HR_history.current}
            ranges={HR_alm.current}
            timestamps={ts_history.current}
            colors={scoringColors}
            min_ts={chart_min_ts}
            max_ts={chart_max_ts}
            current_ts={current_ts}
          />
        )}
        {selectedParameters.includes("RR") && (
          <ScoringBox
            parm={RR_history.current}
            ranges={RR_alm.current}
            timestamps={ts_history.current}
            colors={scoringColors}
            min_ts={chart_min_ts}
            max_ts={chart_max_ts}
            current_ts={current_ts}
          />
        )}
        {selectedParameters.includes("Temp1") && (
          <ScoringBox
            parm={Temp1_history.current}
            ranges={Temp1_alm.current}
            timestamps={ts_history.current}
            colors={scoringColors}
            min_ts={chart_min_ts}
            max_ts={chart_max_ts}
            current_ts={current_ts}
          />
        )}
        {!isPharlap && (
          <>
            {selectedParameters.includes("EtCO2") && (
              <ScoringBox
                parm={EtCO2_history.current}
                ranges={EtCO2_alm}
                timestamps={ts_history.current}
                colors={scoringColors}
                min_ts={chart_min_ts}
                max_ts={chart_max_ts}
                current_ts={current_ts}
              />
            )}
            {useNeuroparms && selectedParameters.includes("ACVPU") && (
              <ScoringBox
                parm={ACVPU_history.current}
                ranges={ACVPU_alm.current}
                timestamps={ts_history.current}
                colors={scoringColors}
                min_ts={chart_min_ts}
                max_ts={chart_max_ts}
                current_ts={current_ts}
              />
            )}
            {useNeuroparms && selectedParameters.includes("RespDistress") && (
              <ScoringBox
                parm={RespDistress_history.current}
                ranges={RespDistress_alm.current}
                timestamps={ts_history.current}
                colors={scoringColors}
                min_ts={chart_min_ts}
                max_ts={chart_max_ts}
                current_ts={current_ts}
              />
            )}
            {useActivityLevel && selectedParameters.includes("Act") && (
              <ScoringBox
                parm={Act_history.current}
                ranges={Act_alm.current}
                timestamps={ts_history.current}
                colors={scoringColors}
                min_ts={chart_min_ts}
                max_ts={chart_max_ts}
                current_ts={current_ts}
              />
            )}
          </>
        )}
      </Grid>
      {/* -------------------------------- DIALOGS -------------------------------- */}
      <Dialog open={openNewChartDialog}>
        <DialogTitle> {t("START NEW CHART")} </DialogTitle>
        <DialogContent>
          <Typography ml={1}>{t("confirm_start_new_chart")}</Typography>
          <Typography ml={1}>{t("this_will_erase_all_history_data")}</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseNo}> {t("no")}</Button>
          <Button onClick={handleCloseYes}> {t("yes")}</Button>
        </DialogActions>
      </Dialog>{" "}
      {/* Start New Chart */}
      <Snackbar
        open={openExportSnackbar}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <Alert sx={{ padding: 4 }} severity="success">
          {t("GENERATING FILE")}
        </Alert>
      </Snackbar>
      <DialogCaseId
        device={device}
        openDialog={openCaseDialog}
        setOpenDialog={setOpenCaseDialog}
      />
      {/* Change Case ID dialog */}
      <Dialog open={openExportDialog}>
        <DialogTitle> {t("export_chart_to_file")} </DialogTitle>
        <DialogContent>
          {false && (
            <GranularitySelector
              setParmInterval={setParmInterval}
              defaultval={device.parameter_granularity}
              title={t("enter_parameter_interval")}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExport}> {t("cancel")}</Button>
          <Button variant="contained" onClick={handlePDFExport}>
            {t("Export to PDF")}
          </Button>
          <Button variant="contained" onClick={handleCSVExport}>
            {t("Export to CSV")}
          </Button>
          <Button variant="contained" onClick={handleJSONExport}>
            {t("Export to JSON")}
          </Button>
          <Button variant="contained" onClick={handleXMLExport}>
            {t("Export to XML")}
          </Button>
        </DialogActions>
      </Dialog>{" "}
      {/* Export Dialog */}
    </Grid>
    </div>
  );
};
